import { useEffect, useRef, useState } from "react";
import { DefectImage } from "../../types/StockItem.interface";
import { Loader } from "lucide-react";

interface Props {
    images: DefectImage[],
    showCarousel: boolean,
    setShowCarousel: (show: boolean) => void,
    domain?: string,
}

/**
 * Карусель изображений
 * @returns 
 */
export const ImgCarousel: React.FC<Props> = ({ images, showCarousel, setShowCarousel, domain = '' }) => {
    const [currentImage, setCurrentImage] = useState(0);
    const [isCarouselImgLoaded, setIsCarouselImgLoaded] = useState(false);

    const carouselContainerRef = useRef<HTMLDivElement>(null);

    let carouselTimeout: NodeJS.Timeout;

    //Перерендеринг при смене изображения карусели
    useEffect(() => {
        if (images.length > 1) {
            //Перелистыватель карусели
            carouselTimeout = setTimeout(() => {
                setCurrentImage(old => old + 1 > images.length - 1 ? 0 : old + 1);
                setIsCarouselImgLoaded(false);
            }, 3000);
        }

        /** Перехват нажатий клавиш */
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!showCarousel) return;

            /** Перелистыватель карусели клавиатурой */
            if (images.length > 1) {
                if (event.key === 'ArrowRight') {
                    setCurrentImage(old => old + 1 > images.length - 1 ? 0 : old + 1);
                    setIsCarouselImgLoaded(false);
                } else if (event.key === 'ArrowLeft') {
                    setCurrentImage(old => old - 1 < 0 ? images.length - 1 : old - 1);
                    setIsCarouselImgLoaded(false);
                }
            }

            /** Закрывает карусель клавишей Escape */
            if (event.key === 'Escape') {
                handleCloseCarousel();
            }
        };

        /** Закрывает карусель кликом вне её */
        const handleClick = (event: MouseEvent) => {
            if (showCarousel && event.target === carouselContainerRef.current) {
                handleCloseCarousel();
            }
        };

        //Добавление слушателей событий
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('click', handleClick);

        //Размонтирование
        return () => {
            clearTimeout(carouselTimeout);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('click', handleClick);
        };
    }, [currentImage]);

    /** Закрывает карусель изображений дефектного товара */
    const handleCloseCarousel = () => {
        setShowCarousel(false);
        clearTimeout(carouselTimeout);
    };

    return showCarousel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
            ref={carouselContainerRef}>
            <div className='relative max-w-3xl w-full flex justify-center'>
                <button className='absolute top-2 right-2 text-white text-3xl font-bold z-10 bg-black bg-opacity-40 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70 transition focus:outline-none focus:ring-2 focus:ring-orange-400'
                    onClick={handleCloseCarousel}>
                    x
                </button>
                {/* TODO Реализовать плавное перелистывание */}
                {/* <div className='flex w-full'>
                        {
                            images.map((image) => (
                                <img key={image.id}
                                    src={domain + image.url}
                                    alt="Просмотр"
                                    className='max-h-[80vh] w-full rounded-xl shadow-lg object-contain opacity-100'
                                    style={{ transition: 'opacity 0.2s', gap: '100vw' }} />
                            ))
                        }
                        </div> */}
                {!isCarouselImgLoaded && <Loader className='w-8 h-8 animate-spin text-orange-500' />}
                <img src={domain + images[currentImage].url}
                    alt="Просмотр"
                    className={isCarouselImgLoaded ? 'max-h-[80vh] w-full rounded-xl shadow-lg object-contain opacity-100' : 'hidden'}
                    onLoad={() => setIsCarouselImgLoaded(true)}
                    style={{ transition: 'opacity 0.2s' }} />
            </div>
        </div>
    );
};